import {
  featuresByGroup,
  formatShowcaseLimit,
} from "@/lib/plans/showcase-features";
import { cn } from "@/lib/utils";
import { HOME_COPY } from "../home.copy";
import type { Plan } from "@/services/plans/types";

interface PlanComparisonSectionProps {
  plans: Plan[];
  highlightedId: string | undefined;
}

/** Rótulos que representam "tem, e sem teto" — merecem o verde do destaque. */
const POSITIVE_LABELS = new Set(["ilimitado", "incluso"]);

/**
 * A mesma lista curada dos chips, agora em matriz — recurso na linha, plano na
 * coluna, limite na célula.
 *
 * Chips e matriz consomem `SHOWCASE_FEATURES`, então não há como divergirem:
 * mudar um limite no admin muda os dois de uma vez, e adicionar um recurso à
 * vitrine o faz aparecer nos dois lugares.
 *
 * A tabela rola no eixo horizontal em telas estreitas em vez de espremer as
 * colunas — três planos não cabem em 375px sem quebrar a leitura do número, e
 * número ilegível numa página de preços é pior que rolagem.
 */
export const PlanComparisonSection = ({
  plans,
  highlightedId,
}: PlanComparisonSectionProps) => {
  const groups = featuresByGroup();

  return (
    <section className="mt-20">
      <div className="mb-7 text-center">
        <h3 className="mb-2 text-3xl font-bold tracking-tight text-tami-light md:text-[38px]">
          {HOME_COPY.planComparison.title}
        </h3>
        <p className="text-sm text-tami-light/60">
          {HOME_COPY.planComparison.subtitle}
        </p>
      </div>

      <div className="-mx-4 overflow-x-auto px-4">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <caption className="sr-only">
            {HOME_COPY.planComparison.caption}
          </caption>

          <thead>
            <tr>
              <th
                scope="col"
                className="w-[34%] border-b-2 border-tami-light/30 px-3.5 py-3 text-left font-bold text-tami-light"
              >
                {HOME_COPY.planComparison.featureColumn}
              </th>
              {plans.map((plan) => (
                <th
                  key={plan.id}
                  scope="col"
                  className={cn(
                    "border-b-2 px-3.5 py-3 text-center font-bold",
                    plan.id === highlightedId
                      ? "rounded-t-xl border-tami-green bg-tami-green text-tami-dark"
                      : "border-tami-light/30 text-tami-light",
                  )}
                >
                  {plan.name}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {groups.flatMap(([group, features]) => [
              <tr key={group}>
                <td
                  colSpan={plans.length + 1}
                  className="px-3.5 pb-2 pt-6 text-[11px] font-bold uppercase tracking-[0.11em] text-tami-light/50"
                >
                  {group}
                </td>
              </tr>,
              ...features.map((feature) => (
                <tr key={feature.key}>
                  <th
                    scope="row"
                    className="border-b border-tami-light/10 px-3.5 py-2.5 text-left font-medium text-tami-light"
                  >
                    {feature.label}
                  </th>
                  {plans.map((plan) => {
                    const resolved = plan.entitlements[feature.key];
                    const label = resolved
                      ? formatShowcaseLimit(plan.entitlements, feature)
                      : "—";

                    return (
                      <td
                        key={plan.id}
                        className={cn(
                          "border-b border-tami-light/10 px-3.5 py-2.5 text-center",
                          plan.id === highlightedId && "bg-tami-green/[0.07]",
                          POSITIVE_LABELS.has(label)
                            ? "font-semibold text-tami-green"
                            : "text-tami-light/85",
                        )}
                      >
                        {label}
                      </td>
                    );
                  })}
                </tr>
              )),
            ])}
          </tbody>
        </table>
      </div>
    </section>
  );
};
